const MEASUREMENT_ID = 'G-TK5H2PWVQ5';
const CONSENT_VERSION = '2026-08-29-v1';
const STORAGE_KEY = 'ev123.analytics.consent';
const RETENTION_MS = 180 * 24 * 60 * 60 * 1000;

type ConsentStatus = 'granted' | 'denied';

type ConsentRecord = {
  version: string;
  status: ConsentStatus;
  savedAt: string;
  expiresAt: string;
};

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
    EV123_ANALYTICS?: {
      openSettings: () => void;
      getConsentState: () => ConsentStatus | 'unset';
    };
  }
}

let analyticsAllowed = false;
let tagLoadStarted = false;
let lastPagePath = '';
let consentOverlay: HTMLElement | null = null;

const isLocalEnvironment =
  window.location.protocol === 'file:' ||
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1';

function consentValues(analyticsStorage: ConsentStatus | 'denied') {
  return {
    analytics_storage: analyticsStorage,
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  };
}

function readConsent(): ConsentRecord | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const record = JSON.parse(raw) as ConsentRecord;
    const expiresAt = Date.parse(record.expiresAt || '');
    if (
      record.version !== CONSENT_VERSION ||
      (record.status !== 'granted' && record.status !== 'denied') ||
      !Number.isFinite(expiresAt) ||
      expiresAt <= Date.now()
    ) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return record;
  } catch {
    return null;
  }
}

function saveConsent(status: ConsentStatus): void {
  const now = Date.now();
  const record: ConsentRecord = {
    version: CONSENT_VERSION,
    status,
    savedAt: new Date(now).toISOString(),
    expiresAt: new Date(now + RETENTION_MS).toISOString(),
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // 브라우저 저장이 차단되어도 현재 페이지에서 선택은 유지한다.
  }
}

function isPublicAnalyticsPath(pathname: string): boolean {
  return !(
    pathname === '/login' ||
    pathname === '/signup' ||
    pathname.startsWith('/my/') ||
    pathname.startsWith('/manage')
  );
}

function safePathname(pathname: string): string {
  const clean = (pathname || '/').split('?')[0].split('#')[0];
  let decoded = clean;
  try {
    decoded = decodeURIComponent(clean);
  } catch {
    // 인코딩된 원문을 아래 패턴으로 검사한다.
  }
  const containsEmail = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i.test(decoded);
  const containsPhone = /(?:\+?\d[\d(). -]{7,}\d)/.test(decoded);
  const containsCredential = /(?:token|secret|auth|code|key)[=:/_-][A-Z0-9._~-]{6,}/i.test(decoded);
  if (containsEmail || containsPhone || containsCredential) return '/redacted';
  return clean.startsWith('/') ? clean : '/';
}

function safeOrigin(): string {
  try {
    const url = new URL(window.location.href);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.origin : '';
  } catch {
    return '';
  }
}

function safeReferrer(currentOrigin: string): string {
  if (!document.referrer) return '';
  try {
    const referrer = new URL(document.referrer);
    if (referrer.protocol !== 'https:' && referrer.protocol !== 'http:') return '';
    return referrer.origin === currentOrigin
      ? `${referrer.origin}${safePathname(referrer.pathname)}`
      : referrer.origin;
  } catch {
    return '';
  }
}

function loadGoogleTag(): boolean {
  if (!analyticsAllowed || tagLoadStarted || isLocalEnvironment) return false;
  tagLoadStarted = true;

  const script = document.createElement('script');
  script.id = 'ev123-ga4-loader';
  script.async = true;
  script.referrerPolicy = 'no-referrer';
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID)}`;
  document.head.appendChild(script);

  const origin = safeOrigin();
  window.gtag('js', new Date());
  window.gtag('config', MEASUREMENT_ID, {
    send_page_view: false,
    page_location: `${origin}${safePathname(window.location.pathname)}`,
    page_referrer: safeReferrer(origin),
  });
  return true;
}

function deleteAnalyticsCookies(): void {
  let names: string[] = [];
  try {
    names = document.cookie
      .split(';')
      .map((part) => part.split('=')[0].trim())
      .filter((name) => name === '_ga' || name.startsWith('_ga_'));
  } catch {
    return;
  }
  const hostname = window.location.hostname;
  const parentDomain = hostname.replace(/^www\./i, '');
  for (const name of names) {
    for (const domain of ['', hostname, `.${hostname}`, parentDomain, `.${parentDomain}`]) {
      let value = `${name}=; Max-Age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
      if (domain) value += `; domain=${domain}`;
      try {
        document.cookie = value;
      } catch {
        // 가능한 범위에서만 삭제한다.
      }
    }
  }
}

function closeConsentDialog(): void {
  consentOverlay?.remove();
  consentOverlay = null;
}

function stopAnalytics(): void {
  analyticsAllowed = false;
  lastPagePath = '';
  document.getElementById('ev123-ga4-loader')?.remove();
  deleteAnalyticsCookies();
  if (tagLoadStarted) window.setTimeout(() => window.location.reload(), 0);
  tagLoadStarted = false;
}

function chooseConsent(status: ConsentStatus): void {
  saveConsent(status);
  closeConsentDialog();
  if (status === 'granted') {
    analyticsAllowed = true;
    window.gtag('consent', 'update', consentValues('granted'));
    loadGoogleTag();
    trackPageView();
    return;
  }
  stopAnalytics();
}

function makeChoiceButton(label: string, status: ConsentStatus): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'ev123-consent__choice';
  button.textContent = label;
  button.addEventListener('click', () => chooseConsent(status));
  return button;
}

function renderConsentDialog(initial: boolean): void {
  closeConsentDialog();
  const overlay = document.createElement('div');
  overlay.className = 'ev123-consent';

  const dialog = document.createElement('section');
  dialog.className = 'ev123-consent__dialog';
  dialog.setAttribute('role', 'dialog');
  dialog.setAttribute('aria-modal', 'true');
  dialog.setAttribute('aria-labelledby', 'ev123-consent-title');
  dialog.setAttribute('aria-describedby', 'ev123-consent-description');
  dialog.tabIndex = -1;

  const title = document.createElement('h2');
  title.id = 'ev123-consent-title';
  title.textContent = '선택적 방문 분석';

  const description = document.createElement('p');
  description.id = 'ev123-consent-description';
  description.textContent = initial
    ? '익명 방문·페이지 통계를 위해 Google Analytics 사용 여부를 선택해 주세요. 어느 쪽을 선택해도 홈페이지 기능은 동일하게 이용할 수 있습니다.'
    : '분석 사용 여부를 변경할 수 있습니다. 분석을 거부해도 홈페이지 이용에 불이익이 없습니다.';

  const actions = document.createElement('div');
  actions.className = 'ev123-consent__actions';
  actions.append(
    makeChoiceButton('분석 허용', 'granted'),
    makeChoiceButton('필수만 사용', 'denied'),
  );
  dialog.append(title, description, actions);
  overlay.appendChild(dialog);
  document.body.appendChild(overlay);
  consentOverlay = overlay;

  overlay.addEventListener('keydown', (event) => {
    const choices = Array.from(overlay.querySelectorAll<HTMLButtonElement>('button'));
    if (event.key === 'Escape' && !initial) {
      event.preventDefault();
      closeConsentDialog();
      return;
    }
    if (event.key !== 'Tab' || choices.length < 2) return;
    const first = choices[0];
    const last = choices[choices.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
  dialog.focus();
}

export function openAnalyticsSettings(): void {
  renderConsentDialog(false);
}

export function trackPageView(): boolean {
  if (!analyticsAllowed || isLocalEnvironment || !isPublicAnalyticsPath(window.location.pathname)) {
    return false;
  }
  loadGoogleTag();
  const path = safePathname(window.location.pathname);
  if (path === lastPagePath) return false;
  lastPagePath = path;
  const origin = safeOrigin();
  try {
    window.gtag('event', 'page_view', {
      page_location: `${origin}${path}`,
      page_path: path,
      page_referrer: safeReferrer(origin),
    });
    return true;
  } catch {
    return false;
  }
}

export function initAnalyticsConsent(): void {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || ((...args: unknown[]) => window.dataLayer.push(args));
  window.gtag('consent', 'default', consentValues('denied'));

  window.EV123_ANALYTICS = {
    openSettings: openAnalyticsSettings,
    getConsentState: () => readConsent()?.status ?? 'unset',
  };

  const record = readConsent();
  if (!record) {
    renderConsentDialog(true);
    return;
  }
  if (record.status === 'granted') {
    analyticsAllowed = true;
    window.gtag('consent', 'update', consentValues('granted'));
    loadGoogleTag();
  }
}

